import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  effect,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  LucideAngularModule,
  Image,
  ChevronsUp,
  ChevronsDown,
  Info,
} from 'lucide-angular';
import { TooltipModule } from 'primeng/tooltip'; // cspell:disable-line
import { SelectButtonModule } from 'primeng/selectbutton'; // cspell:disable-line
import {
  PageHeaderComponent,
  BreadcrumbItem,
} from '../../../shared/components/page-header/page-header.component';
import { ImpactService } from '../../services/impact.service';
import { TripImpactHeaderComponent } from '../trip-impact-header/trip-impact-header.component';
import { ImpactOverviewComponent } from '../impact-overview/impact-overview.component';
import { CollapsibleSectionComponent } from '../collapsible-section/collapsible-section.component';
import { ContributionCtaComponent } from '../contribution-cta/contribution-cta.component';
import { ImpactPerDayChartComponent } from '../impact-per-day-chart/impact-per-day-chart.component';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'; // cspell:disable-line
import { ColorsService } from '../../services/colors.service';
import { translateElementType } from '../../utils/element-type-translation';
import { ForumService } from '../../../shared/services/forum.service';
import { CurrentUserService } from '../../../shared/services/current-user.service';
import { EcoScoreComponent } from '../eco-score/eco-score.component';
import { Trip } from 'src/app/trip-calculator/models/trip';
import { DetailedImpactReport } from '../../models/DetailedImpactReport';
import { buildChartLogoSources, ChartLogoSource } from '../../models';
import { AdemeEquivalencesComponent } from '../ademe-equivalences/ademe-equivalences.component';
import { NotificationService } from '../../../core/services/notification.service';
import { GreentripperPinIconComponent } from '../../../shared/components/greentripper-pin-icon/greentripper-pin-icon.component';
import { GreentripperLoaderComponent } from '../../../shared/components/greentripper-loader/greentripper-loader.component';
import { PackLimitationsService } from 'src/app/shared/services/pack-limitations.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-trip-impact-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    TooltipModule,
    SelectButtonModule,
    TranslocoModule,
    PageHeaderComponent,
    TripImpactHeaderComponent,
    ImpactOverviewComponent,
    CollapsibleSectionComponent,
    AdemeEquivalencesComponent,
    ContributionCtaComponent,
    ImpactPerDayChartComponent,
    EcoScoreComponent,
    GreentripperPinIconComponent,
    GreentripperLoaderComponent,
  ],
  templateUrl: './trip-impact-page.component.html',
  styles: [],
})
export class TripImpactPageComponent implements OnInit, OnDestroy {
  impactReport: DetailedImpactReport | null = null;
  dailyImpactData: Array<{
    date: string;
    transport: number;
    accommodation: number;
    activity: number;
    food: number;
    waste: number;
  }> = [];
  hasScheduledElements = true;
  showPercentage = false;
  excludeMainTransport = false;
  groupTransports: boolean = false;
  overviewMode: 'detailed' | 'mainVsOnSite' = 'mainVsOnSite';
  allSectionsOpen = true; // All sections start open by default
  breadcrumbs: BreadcrumbItem[] = [];
  hasTimData = false;
  // Intersection Observer pour la capture de lead
  @ViewChild('contributionCta', { read: ElementRef })
  contributionCtaRef!: ElementRef;
  private intersectionObserver?: IntersectionObserver;
  private forumService = inject(ForumService);
  private currentUserService = inject(CurrentUserService);
  private routeParamsSubscription?: Subscription;
  protected packLimitationService = inject(PackLimitationsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  protected impactService = inject(ImpactService);
  private transloco = inject(TranslocoService);
  private colorsService = inject(ColorsService);
  private notificationService = inject(NotificationService);

  /** Logos proposés à l'export PDF : mêmes sources que le popover du graphique. */
  getLogoSources(): ChartLogoSource[] {
    return buildChartLogoSources(
      this.impactReport?.company || null,
      this.packLimitationService.canFilterClient()
        ? this.impactReport?.clients || []
        : [],
    );
  }

  // Icons
  imageIcon = Image;
  chevronsUpIcon = ChevronsUp;
  chevronsDownIcon = ChevronsDown;
  infoIcon = Info;

  // Options pour SelectButton
  viewModeOptions = [
    {
      label: this.transloco.translate('impact.viewMode.group'),
      value: 'group',
    },
    {
      label: this.transloco.translate('impact.viewMode.individual'),
      value: 'individual',
    },
  ];

  constructor() {
    // The report is also reloaded from outside this component — applying
    // reduction tips rewrites the trip and reads it back — so the page follows
    // the service signal rather than only its own request.
    effect(() => {
      const report = this.impactService.detailedReport();
      if (report) this.onReportLoaded(report);
    });
  }

  ngOnInit() {
    this.setupBreadcrumbs();
    // Subscribe to route params to handle direct navigation
    this.routeParamsSubscription = this.route.params.subscribe({
      next: (params) => {
        const tripId = params['id'];
        if (tripId) {
          this.loadDetailedImpactReport(tripId);
        }
      },
      error: (error) => {
        console.error('Error loading route params:', error);
      },
    });
  }

  t(key: string): string {
    return this.transloco.translate(key);
  }

  private setupBreadcrumbs() {
    this.breadcrumbs = [
      {
        label: 'Accueil',
        route: '/home',
        translationKey: 'navigation.home',
      },
      {
        label: 'Voyages',
        route: '/home/travel-board',
        translationKey: 'navigation.trips',
      },
      {
        label: "Rapport d'impact",
        translationKey: 'impactReport.title',
      },
    ];
  }

  private loadDetailedImpactReport(tripId: string) {
    this.impactService.getDetailedImpactReport(tripId).subscribe({
      // The report reaches `onReportLoaded()` through the service signal.
      next: () => undefined,
      error: (error) => {
        console.error('Error loading detailed impact report:', error);
        // Redirect to dashboard if impact not calculated (400) or trip not found (404)
        if (error.status === 400 || error.status === 404) {
          this.notificationService.error(
            this.transloco.translate('impact.errors.notCalculatedYet'),
          );
          this.router.navigate(['/home/trips', tripId, 'dashboard']);
        }
      },
    });
  }

  private onReportLoaded(response: DetailedImpactReport) {
    // The service holds the last report loaded app-wide, which on arrival is
    // still the previous trip's — ignore anything but the trip in the route.
    if (response.trip.id !== this.route.snapshot.params['id']) return;

    // Redirect to dashboard if no impact data (draft trip or broken link)
    if (!response.trip.tripImpact) {
      console.warn('No impact data available, redirecting to trip dashboard');
      this.router.navigate(['/home/trips', response.trip.id, 'dashboard']);
      return;
    }

    this.impactReport = response;
    !environment.production && console.log(response);
    // Valider la cohérence des données reçues
    this.validateDataConsistency(response);
    this.checkForTimData(response);
    // Use daily impact data from backend if available
    if (response.dailyImpactData && response.dailyImpactData.length > 0) {
      this.dailyImpactData = response.dailyImpactData.map((dayData) => ({
        date: `Jour ${dayData.day}`,
        transport: dayData.transport,
        accommodation: dayData.accommodation,
        activity: dayData.activity,
        food: dayData.food,
        waste: dayData.waste,
      }));

      // Check if any day has non-waste impact (transport, accommodation, activity, food)
      this.hasScheduledElements = response.dailyImpactData.some(
        (dayData) =>
          dayData.transport > 0 ||
          dayData.accommodation > 0 ||
          dayData.activity > 0 ||
          dayData.food > 0,
      );
    } else {
      // Fallback to old method if no daily data from backend
      this.hasScheduledElements = false;
      this.buildDailyDataFallback(response.trip);
    }
    // Update the last breadcrumb with the trip title
    if (response.trip.title) {
      this.breadcrumbs[this.breadcrumbs.length - 1] = {
        label: response.trip.title,
      };
    }

    // Initialiser l'Intersection Observer pour la capture de lead automatique
    // On attend un petit délai pour s'assurer que le ViewChild est résolu
    setTimeout(() => {
      this.setupContributionModalTrigger();
    }, 100);
  }

  private validateDataConsistency(report: DetailedImpactReport) {
    if (!report.trip.tripImpact || !report.fullImpactDetails) {
      console.warn('Missing impact data for validation');
      return;
    }

    const tripImpact = report.trip.tripImpact;
    const fullDetails = report.fullImpactDetails;

    // Calculer les totaux depuis les détails
    const transportSum = fullDetails.transport.reduce(
      (sum, item) => sum + item.impact,
      0,
    );
    const accommodationSum = fullDetails.accommodation.reduce(
      (sum, item) => sum + item.impact,
      0,
    );
    const activitiesSum = fullDetails.activities.reduce(
      (sum, item) => sum + item.impact,
      0,
    );
    const foodSum = fullDetails.food.reduce(
      (sum, item) => sum + item.impact,
      0,
    );

    const tolerance = 0.1; // Tolérance de 0.1 kg CO2e

    // Valider chaque catégorie
    if (Math.abs(transportSum - tripImpact.transportImpact) > tolerance) {
      console.warn(`Frontend validation: Transport impact mismatch`, {
        calculated: transportSum,
        expected: tripImpact.transportImpact,
        difference: Math.abs(transportSum - tripImpact.transportImpact),
      });
    }

    if (
      Math.abs(accommodationSum - tripImpact.accommodationImpact) > tolerance
    ) {
      console.warn(`Frontend validation: Accommodation impact mismatch`, {
        calculated: accommodationSum,
        expected: tripImpact.accommodationImpact,
        difference: Math.abs(accommodationSum - tripImpact.accommodationImpact),
      });
    }

    if (Math.abs(activitiesSum - tripImpact.activityImpact) > tolerance) {
      console.warn(`Frontend validation: Activities impact mismatch`, {
        calculated: activitiesSum,
        expected: tripImpact.activityImpact,
        difference: Math.abs(activitiesSum - tripImpact.activityImpact),
      });
    }

    if (Math.abs(foodSum - tripImpact.foodImpact) > tolerance) {
      console.warn(`Frontend validation: Food impact mismatch`, {
        calculated: foodSum,
        expected: tripImpact.foodImpact,
        difference: Math.abs(foodSum - tripImpact.foodImpact),
      });
    }

    // Valider le total général
    const calculatedTotal =
      transportSum +
      accommodationSum +
      activitiesSum +
      foodSum +
      tripImpact.wasteImpact;
    if (Math.abs(calculatedTotal - tripImpact.totalImpact) > tolerance) {
      console.warn(`Frontend validation: Total impact mismatch`, {
        calculated: calculatedTotal,
        expected: tripImpact.totalImpact,
        difference: Math.abs(calculatedTotal - tripImpact.totalImpact),
        breakdown: {
          transport: transportSum,
          accommodation: accommodationSum,
          activities: activitiesSum,
          food: foodSum,
          waste: tripImpact.wasteImpact,
        },
      });
    }
  }
  private checkForTimData(report: DetailedImpactReport) {
    const transports = report.trip.transports;
    for (const transport of transports ?? []) {
      if (
        transport.carrierCode &&
        transport.flightNumber &&
        transport.flightDate
      ) {
        this.hasTimData = true;
        break;
      }
    }
  }

  onShowPercentageChange(value: boolean | Event) {
    if (typeof value === 'boolean') {
      this.showPercentage = value;
    } else {
      this.showPercentage = (value.target as HTMLInputElement).checked;
    }
  }

  onExcludeMainTransportChange(value: boolean | Event) {
    if (typeof value === 'boolean') {
      this.excludeMainTransport = value;
    } else {
      this.excludeMainTransport = (value.target as HTMLInputElement).checked;
    }
  }

  onGroupTransportsChange(checked: boolean) {
    this.groupTransports = checked;
  }

  getTransportChartData() {
    const fullElements = this.impactReport?.fullImpactDetails.transport || [];
    if (fullElements.length === 0) return [];

    const travelers = this.impactReport?.trip.travellers || 1;
    const adjustedElements = fullElements
      .map((el) => ({
        ...el,
        adjustedImpact:
          this.impactService.viewMode() === 'individual'
            ? el.impact / travelers
            : el.impact,
      }))
      .sort((a, b) => b.adjustedImpact - a.adjustedImpact);

    const colors = this.colorsService.generateCategoryGradient(
      'transport',
      adjustedElements.length,
    );

    const totalImpact = adjustedElements.reduce(
      (sum, el) => sum + el.adjustedImpact,
      0,
    );

    return adjustedElements.map((element, index) => ({
      label: translateElementType(this.transloco, element.type),
      value: this.showPercentage
        ? Math.round((element.adjustedImpact / totalImpact) * 100)
        : Math.round(element.adjustedImpact),
      color: colors[index] || this.colorsService.TRANSPORT_COLOR,
      distance: `${Math.round(element.km)} km`,
      title: element.title,
    }));
  }

  getAccommodationChartData() {
    const fullElements =
      this.impactReport?.fullImpactDetails.accommodation || [];
    if (fullElements.length === 0) return [];

    const travelers = this.impactReport?.trip.travellers || 1;
    const adjustedElements = fullElements
      .map((el) => ({
        ...el,
        adjustedImpact:
          this.impactService.viewMode() === 'individual'
            ? el.impact / travelers
            : el.impact,
      }))
      .sort((a, b) => b.adjustedImpact - a.adjustedImpact);

    const colors = this.colorsService.generateCategoryGradient(
      'accommodation',
      adjustedElements.length,
    );

    const totalImpact = adjustedElements.reduce(
      (sum, el) => sum + el.adjustedImpact,
      0,
    );

    return adjustedElements.map((element, index) => ({
      label: translateElementType(this.transloco, element.type),
      value: this.showPercentage
        ? Math.round((element.adjustedImpact / totalImpact) * 100)
        : Math.round(element.adjustedImpact),
      color: colors[index] || this.colorsService.ACCOMMODATION_COLOR,
      title: element.title,
      isEcoFriendly: element.isEcoFriendly,
    }));
  }

  getActivitiesChartData() {
    const fullElements = this.impactReport?.fullImpactDetails.activities || [];
    if (fullElements.length === 0) return [];

    const travelers = this.impactReport?.trip.travellers || 1;
    const adjustedElements = fullElements
      .map((el) => ({
        ...el,
        adjustedImpact:
          this.impactService.viewMode() === 'individual'
            ? el.impact / travelers
            : el.impact,
      }))
      .sort((a, b) => b.adjustedImpact - a.adjustedImpact);

    const colors = this.colorsService.generateCategoryGradient(
      'activity',
      adjustedElements.length,
    );

    const totalImpact = adjustedElements.reduce(
      (sum, el) => sum + el.adjustedImpact,
      0,
    );

    return adjustedElements.map((element, index) => ({
      label: translateElementType(this.transloco, element.type),
      value: this.showPercentage
        ? Math.round((element.adjustedImpact / totalImpact) * 100)
        : Math.round(element.adjustedImpact),
      color: colors[index] || this.colorsService.ACTIVITY_COLOR,
      title: element.title,
    }));
  }

  getFoodChartData() {
    const fullElements = this.impactReport?.fullImpactDetails.food || [];
    if (fullElements.length === 0) return [];

    // Regrouper par type de nourriture
    const groupedData: { [type: string]: { impact: number; count: number } } =
      {};
    fullElements.forEach((el) => {
      if (!groupedData[el.type]) {
        groupedData[el.type] = { impact: 0, count: 0 };
      }
      groupedData[el.type].impact += el.impact;
      groupedData[el.type].count += 1;
    });

    // Créer et trier les éléments par impact décroissant
    const groupedElements = Object.entries(groupedData)
      .map(([type, data]) => ({
        type,
        impact: data.impact,
        count: data.count,
      }))
      .sort((a, b) => b.impact - a.impact);

    // Générer des couleurs dégradées pour la nourriture triée par impact
    const colors = this.colorsService.generateCategoryGradient(
      'food',
      groupedElements.length,
    );

    // Les données viennent déjà du backend avec les bonnes valeurs
    const adjustedElements = groupedElements.map((el) => ({
      ...el,
      adjustedImpact:
        this.impactService.viewMode() === 'individual'
          ? el.impact / (this.impactReport?.trip.travellers || 1)
          : el.impact,
    }));

    const totalImpact = adjustedElements.reduce(
      (sum, el) => sum + el.adjustedImpact,
      0,
    );

    return adjustedElements.map((element, index) => ({
      label: translateElementType(this.transloco, element.type),
      value: this.showPercentage
        ? Math.round((element.adjustedImpact / totalImpact) * 100)
        : Math.round(element.adjustedImpact),
      color: colors[index] || this.colorsService.FOOD_COLOR,
    }));
  }

  toggleAllSections() {
    this.allSectionsOpen = !this.allSectionsOpen;
  }

  private buildDailyDataFallback(trip: Trip) {
    // Fallback method using existing service
    this.impactService
      .buildImpactPerDayChartData(trip)
      .then((chartData) => {
        const labels: string[] = (chartData.labels as string[]) || [];
        const datasets = chartData.datasets || [];
        const days = labels.length;
        const transport =
          (datasets.find((d) => d.label === 'transport')?.data as number[]) ||
          Array(days).fill(0);
        const accommodation =
          (datasets.find((d) => d.label === 'accommodation')
            ?.data as number[]) || Array(days).fill(0);
        const activity =
          (datasets.find((d) => d.label === 'activity')?.data as number[]) ||
          Array(days).fill(0);
        const food =
          (datasets.find((d) => d.label === 'meal')?.data as number[]) ||
          Array(days).fill(0);
        const waste =
          (datasets.find((d) => d.label === 'waste')?.data as number[]) ||
          Array(days).fill(0);

        this.dailyImpactData = labels.map((label, i) => ({
          date: label,
          transport: transport[i] || 0,
          accommodation: accommodation[i] || 0,
          activity: activity[i] || 0,
          food: food[i] || 0,
          waste: waste[i] || 0,
        }));
      })
      .catch((e) => console.error('Error building per-day data', e));
  }

  private setupContributionModalTrigger() {
    // Ne pas installer l'observer si pas de données de voyage
    if (!this.impactReport?.trip) return;

    const tripId = this.impactReport.trip.id;
    if (!tripId) return;

    // Vérifier si la company contient "demo-forum" dans le nom
    const currentUser = this.currentUserService.currentUser();
    const isDemoForum = currentUser?.companyName
      ?.toLowerCase()
      .includes('demo-forum');

    if (!isDemoForum) {
      return; // Ne déclencher que pour les comptes demo-forum
    }

    // Pour les comptes demo-forum (tablettes commerciales), vérifier sessionStorage
    // pour éviter de montrer plusieurs fois pour le même voyage dans la même session
    // Pour les autres comptes (si on étend plus tard), on garde la vérification
    const sessionKey = `forum_modal_shown_trip_${tripId}`;
    if (!isDemoForum && sessionStorage.getItem(sessionKey)) {
      return; // Déjà montré dans cette session (seulement pour les comptes non-demo)
    }

    // Créer l'Intersection Observer
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
            // Attendre 1 seconde avant d'ouvrir la modale pour ne pas surprendre l'utilisateur
            setTimeout(() => {
              this.triggerForumModal();
            }, 1000);

            // Déconnecter l'observer après déclenchement
            if (this.intersectionObserver) {
              this.intersectionObserver.disconnect();
            }
          }
        });
      },
      { threshold: 0.3 }, // Se déclenche quand le CTA est visible à 30%
    );

    // Observer le composant CTA s'il existe
    if (this.contributionCtaRef?.nativeElement) {
      this.intersectionObserver.observe(this.contributionCtaRef.nativeElement);
    }
  }

  private triggerForumModal() {
    if (!this.impactReport?.trip) return;

    const tripName = this.impactReport.trip.title || 'Voyage calculé';
    const co2Impact =
      this.impactReport.trip.tripImpact?.impactPerTraveller || 0;
    const tripId = this.impactReport.trip.id;

    // Marquer comme montré dans cette session seulement pour les comptes non-demo
    const currentUser = this.currentUserService.currentUser();
    const isDemoForum = currentUser?.companyName
      ?.toLowerCase()
      .includes('demo-forum');

    if (tripId && !isDemoForum) {
      sessionStorage.setItem(`forum_modal_shown_trip_${tripId}`, 'true');
    }

    // Déclencher la modale
    this.forumService.showForumModal(tripName, co2Impact);
  }

  ngOnDestroy() {
    // Nettoyer l'observer si il existe
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    // Unsubscribe from route params
    if (this.routeParamsSubscription) {
      this.routeParamsSubscription.unsubscribe();
    }
  }
}
